import type { SceneState } from './studioState';

/** A scene as the build view draws it: its state, its newest saved preview, and the plan's sketch of it. */
export interface BuildScene extends SceneState {
  sketch: string;
}

/** m:ss.t, the way an editor reads lengths. */
export function timecode(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = (sec - m * 60).toFixed(1).padStart(4, '0');
  return `${m}:${s}`;
}

/** The scene the frame follows: the newest draft being built, else the first still building, else the last built. */
export function pickFocusScene(scenes: BuildScene[]): BuildScene | null {
  if (scenes.length === 0) return null;
  const building = scenes.filter((s) => s.status === 'building');
  const drafted = building.filter((s) => s.lastDraftAt).sort((a, b) => (b.lastDraftAt ?? 0) - (a.lastDraftAt ?? 0));
  if (drafted.length) return drafted[0];
  if (building.length) return building[0];
  const built = scenes.filter((s) => s.status === 'built');
  if (built.length) return built[built.length - 1];
  return scenes[0];
}

/** Relative widths for the scene strip: narration length, with a floor so a short scene stays visible. */
export function sceneWeights(scenes: BuildScene[]): number[] {
  const known = scenes.map((s) => s.durationSec).filter((d): d is number => typeof d === 'number' && d > 0);
  if (known.length === 0) return scenes.map(() => 1);
  const fallback = known.reduce((a, b) => a + b, 0) / known.length;
  return scenes.map((s) => Math.max(3, s.durationSec && s.durationSec > 0 ? s.durationSec : fallback));
}
